import { toDictionary } from '@anecdotes/utils';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  NEVER,
  Observable,
  Subscription,
  combineLatest,
  delay,
  filter,
  from,
  isObservable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { DataManipulationConfig } from '../../models';
import { FilterDefinitionStoreService } from '../../services';
import { DataFilterAction } from '../actions';
import { isFilterFieldGroup } from '../../utils';

@Injectable()
export class FilterDefinitionEffects {
  private initializedFilters = new Map<string, Subscription>();

  constructor(
    private filterDefinitionStoreService: FilterDefinitionStoreService,
    private actions: Actions,
    private store: Store
  ) {}

  initFilterInstances$ = createEffect(
    () =>
      this.filterDefinitionStoreService.getAllFilterInstances().pipe(
        tap((filterConfigs) => {
          const dataManipulationConfigs: DataManipulationConfig[] = Object.values(filterConfigs);

          if (Object.keys(filterConfigs).join() !== Object.keys(this.initializedFilters).join()) {
            dataManipulationConfigs.forEach((config) => {
              if (!this.initializedFilters.has(config.filterInstanceId)) {
                this.initFilter(config);
              }
            });
          }
        })
      ),
    { dispatch: false }
  );

  filterInstanceDestroy$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(DataFilterAction.destroy),
        tap((action) => this.destroyFilters(action.instanceId)),
        switchMap(() => NEVER)
      );
    },
    { dispatch: false }
  );

  private initFilter(dataManipulationConfig: DataManipulationConfig): void {
    const fieldsLoadingState = toDictionary(
      dataManipulationConfig.fields.flatMap((fieldOrGroup) => isFilterFieldGroup(fieldOrGroup)),
      (field) => field.fieldId,
      (field) => !!field.isLoaded$
    );

    this.store.dispatch(
      DataFilterAction.init({
        instanceId: dataManipulationConfig.filterInstanceId,
        queryParamsDisabled: dataManipulationConfig.queryParamsDisabled,
        loadingState: fieldsLoadingState,
      })
    );

    const streams = dataManipulationConfig.fields.flatMap((fieldOrGroup) => isFilterFieldGroup(fieldOrGroup))
      .filter((field) => field.isLoaded$)
      .map((field) => {
        let stream$: Observable<any>;

        if (isObservable(field.isLoaded$)) {
          // will treat filter field as loaded only when this stream$ emits true
          stream$ = field.isLoaded$.pipe(filter(Boolean));
        } else if ((field.isLoaded$ as any) instanceof Promise) {
          // will treat filter field as loaded only when this promise resolves (to any value)
          stream$ = from(field.isLoaded$);
        }

        return stream$.pipe(
          take(1),
          // we should delay fields loading because otherwise it will override filter related query params
          // and lazy loaded fields won't be initally set
          delay(1000),
          tap(() =>
            this.store.dispatch(
              DataFilterAction.fieldDataLoaded({
                instanceId: dataManipulationConfig.filterInstanceId,
                fieldId: field.fieldId,
              })
            )
          )
        );
      });

    const subscription = combineLatest(streams).subscribe();
    this.initializedFilters.set(dataManipulationConfig.filterInstanceId, subscription);
  }

  private destroyFilters(filterInstanceId: string): void {
    this.filterDefinitionStoreService.remove(filterInstanceId);
    this.initializedFilters.get(filterInstanceId)?.unsubscribe();
    this.initializedFilters.delete(filterInstanceId);
  }
}
